import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Youtube, Twitter, Linkedin } from "lucide-react";
import { BRAND } from "@/data/brand";
import PshlWordmark from "@/components/PshlWordmark";

const FooterCol = ({ title, links }) => (
  <div>
    <h4 className="font-display text-xs tracking-[0.22em] text-white/60 uppercase mb-4">{title}</h4>
    <ul className="space-y-3">
      {links.map((l) => (
        <li key={l.to + l.label}>
          <Link
            to={l.to}
            data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="text-sm text-white/85 hover:text-pshl-electric transition-colors"
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export default function Footer() {
  return (
    <footer data-testid="pshl-footer" className="relative mt-20 border-t border-white/10 bg-[#04050A]">
      <div className="speed-lines absolute inset-0 opacity-50 pointer-events-none" />
      <div className="relative pshl-container py-10">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-5">
              <img
                src="/pshl8-header-mark.png"
                alt="PSHL∞ — Pro Skate Hockey League"
                draggable={false}
                className="h-12 w-auto select-none"
              />
            </Link>
            <p className="text-sm text-white/65 max-w-md leading-relaxed">
              India’s professional skate hockey franchise league. Commercializing the sport
              through city-based teams, sponsorships, academies, and media.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                { href: "#", Icon: Instagram, label: "instagram" },
                { href: "#", Icon: Youtube, label: "youtube" },
                { href: "#", Icon: Twitter, label: "twitter" },
                { href: "#", Icon: Linkedin, label: "linkedin" },
              ].map(({ href, Icon, label }) => (
                <a
                  key={label}
                  href={href}
                  data-testid={`footer-social-${label}`}
                  aria-label={label}
                  className="h-10 w-10 grid place-items-center border border-white/15 text-white/80 hover:text-white hover:border-pshl-electric hover:bg-pshl-electric/10 transition-colors"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <FooterCol
              title="League"
              links={[
                { label: "About PSHL", to: "/about" },
                { label: "Teams", to: "/teams" },
                { label: "Schedule", to: "/schedule" },
                { label: "Standings", to: "/standings" },
                { label: "News", to: "/news" },
              ]}
            />
          </div>
          <div className="lg:col-span-2">
            <FooterCol
              title="Commercial"
              links={[
                { label: "Sponsorships", to: "/sponsorships" },
                { label: "Franchise", to: "/sponsorships#franchise" },
                { label: "Partnerships", to: "/partnerships" },
                { label: "Academies", to: "/academies" },
                { label: "Media", to: "/media" },
              ]}
            />
          </div>
          <div className="lg:col-span-4">
            <h4 className="font-display text-xs tracking-[0.22em] text-white/60 uppercase mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3 text-white/85">
                <Mail className="h-4 w-4 mt-0.5 text-pshl-electric" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-white">{BRAND.email}</a>
              </li>
              <li className="flex items-start gap-3 text-white/85">
                <Phone className="h-4 w-4 mt-0.5 text-pshl-electric" />
                <a href={`tel:${BRAND.phone}`} className="hover:text-white">{BRAND.phone}</a>
              </li>
              <li className="flex items-start gap-3 text-white/85">
                <MapPin className="h-4 w-4 mt-0.5 text-pshl-electric" />
                <span>League HQ — Gurugram, India</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} {BRAND.fullName} · PSHL8. All rights reserved.
          </div>
          <div className="font-display text-[11px] tracking-[0.4em] uppercase text-white/60">
            {BRAND.tagline} <span className="text-pshl-electric">|</span> {BRAND.subTagline}
          </div>
        </div>
      </div>
    </footer>
  );
}
